'use client'

import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperInstance } from 'swiper';
import 'swiper/css';
import "./some.css"
import { useState, useEffect, useRef } from 'react';
import { Keyboard, Mousewheel, Pagination } from "swiper/modules";
import 'swiper/css/mousewheel'
import "../(book-details)/BookDetail/book-detail.css"
import BookTransition from '../BookTransition/BookTransition';
import BookDetails from '../(book-details)/BookDetail/BookDetail';
import { BookInterface } from '../books/types/book';
import 'swiper/css/pagination';
import { track } from '@vercel/analytics';
import { useBodyClass, useIsClient } from '@/hooks/useBrowser';
import { useBookData } from '../books/utils/imageLoaders';

interface BookCarouselProps {
  books: BookInterface[];
  activeBook: BookInterface | null;
  onActiveBookChange: (book: BookInterface) => void;
  initialSlideNumber?: number;
  isDetailsOpen?: boolean;
  onToggleDetails?: () => void;
}

const BookCarousel = ({ books, activeBook, onActiveBookChange, initialSlideNumber = 0, isDetailsOpen = false,
  onToggleDetails }:
   BookCarouselProps) => {
  const swiperRef = useRef<SwiperInstance | null>(null);
  const [internalIsDetailsOpen, setInternalIsDetailsOpen] = useState(isDetailsOpen);
  const [recentlyChanged, setRecentlyChanged] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isClient = useIsClient()
  const { bookHeights, spineColours } = useBookData(books, 300);
  useBodyClass('book-details-open', isDetailsOpen)

  useEffect(() => {
    if (!isClient) return;

    const setActiveSlideHeight = () => {
      const activeSlide = document.querySelector('.swiper-slide-active');
      if (activeSlide instanceof HTMLElement && activeBook) {
          activeSlide.style.height = `${bookHeights[activeBook.work_urn]}px`;
      }
    };
  
    setActiveSlideHeight();
  }, [activeBook, bookHeights, isClient]);

  // Cleanup timeout on unmount
  useEffect(() => {
    if (!isClient) return;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isClient]);

  // Click handler with protection against immediate opening
  const handleBookClick = (isActive: boolean) => {
    if (isActive) {
      if (!recentlyChanged) {
        if (onToggleDetails) {
          onToggleDetails();
        } else {
          setInternalIsDetailsOpen(true);
        }
        track("book details opened");
      } else {
        // If we're in the cooldown period, consume the click
        // without opening details
        setRecentlyChanged(false);
      }
    }
  };
  useEffect(() => {
    if (onToggleDetails) {
      setInternalIsDetailsOpen(isDetailsOpen);
    }
  }, [isDetailsOpen, onToggleDetails]);
  const handleToggleDetails = () => {
    if (onToggleDetails) {
      onToggleDetails();
    } else {
      setInternalIsDetailsOpen(!internalIsDetailsOpen);
    }
  };
  
  return (
    <>
    <div className="transform-gpu">
      <div className="container">
        <Swiper
          className={`transition-transform duration-500 ${
            isDetailsOpen ? '-translate-x-96' : 'translate-x-0'
          }`}
          modules={[Keyboard, Mousewheel, Pagination]}
          loop={true}
          slidesPerView={10}
          centeredSlides={true}
          slideToClickedSlide={true}    
          spaceBetween={30}
          initialSlide={initialSlideNumber}
          mousewheel={true}
          pagination={{
            dynamicBullets: true,
          }}
          watchSlidesProgress={true}
          watchOverflow={true}
          normalizeSlideIndex={true}
          keyboard={true}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          onSlideChangeTransitionStart={() => {
            // Mark the beginning of a slide change
            setRecentlyChanged(true);
            
            // Clear any existing timeout
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
          }}
          onSlideChangeTransitionEnd={() => {
            // Set a short timeout before resetting the flag
            // This gives a brief window where clicks won't open details
            timeoutRef.current = setTimeout(() => {
              setRecentlyChanged(false);
            }, 100); // A very short cooldown period
          }}
          onSlideChange={(swiper) => {
            const newActiveBook = books[swiper.realIndex];

            onActiveBookChange(newActiveBook);
          }}>
          <div className="swiper-wrapper">
            {books.map((book, index) => (
              <SwiperSlide key={index}>
                {({ isActive }) => (
                  <BookTransition
                    book={book}
                    isActive={isActive}
                    isOpen={isDetailsOpen}
                    onOpen={() => handleBookClick(isActive)}
                    height={bookHeights[book.work_urn]}
                    spineColor={spineColours[book.work_urn]}
                    isMobile={false}
                  />
                )}
              </SwiperSlide>
            ))}
          </div>    
        </Swiper>
      </div>
    </div>
    {activeBook && !isDetailsOpen && (
      <div className="text-center mt-6 transition-opacity duration-300 book-carosel-details">
        <p className="book-title-carosel">{activeBook.title}</p>
        <p className="book-author-carosel">{activeBook.author.split(/[;,]/)[0]}</p>
      </div>
    )}
    <BookDetails
        book={activeBook}
        isOpen={isDetailsOpen}
        onClose={handleToggleDetails}      />
    </>
  );
};

export default BookCarousel;